import React, { useEffect, useState, useRef } from "react";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import axios from "axios";
import "../assets/css/package-comparison-preview.css";

const PackageComparisonPreview = () => {
  const [packageData, setPackageData] = useState(null);
  const [currentView, setCurrentView] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("Export");
  const [showDropdown, setShowDropdown] = useState(false);

  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchPackageData();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchPackageData = async () => {
    try {
      setLoading(true);
      
      // Construct the app URL from the admin URL environment variable
      // REACT_APP_URL is typically ".../api/v1/admin/"
      const adminUrl = process.env.REACT_APP_URL || "";
      const baseUrl = adminUrl.replace(/\/admin\/?$/, "");
      const url = `${baseUrl}/app/package-comparison`;

      const response = await axios.get(url);
      if (response.data && response.data.success && response.data.data) {
        setPackageData(response.data.data);
      } else {
        setError("No package details available.");
      }
    } catch (err) {
      console.error("Error fetching package data:", err);
      setError("Failed to load package details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const prepareExportElement = () => {
    if (!containerRef.current) return null;

    // Create a clone for export to modify styles without affecting UI
    const element = containerRef.current.cloneNode(true);
    
    // Remove controls and buttons from the clone
    const controls = element.querySelector('.pcp-header-controls');
    if (controls) controls.remove();
    
    // Center header
    const header = element.querySelector('.pcp-header');
    if (header) header.style.justifyContent = 'center';
    
    // Fix scrolling content
    const content = element.querySelector('.pcp-content');
    if (content) {
      content.style.maxHeight = 'none';
      content.style.overflow = 'visible';
    }

    return element;
  };

  const exportToPDF = async () => {
    setExporting(true);
    setExportStatus("Generating PDF...");
    setShowDropdown(false);

    try {
      const element = prepareExportElement();
      if (!element) return;

      // Temporary container off-screen
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '1200px';
      tempDiv.appendChild(element);
      document.body.appendChild(tempDiv);

      const filename = currentView === 'all' 
        ? 'Package_Comparison.pdf'
        : `${currentView.charAt(0).toUpperCase() + currentView.slice(1)}_Package.pdf`;

      // Calculate dimensions to create a single continuous page
      const contentWidth = element.scrollWidth;
      const contentHeight = element.scrollHeight;
      
      // Convert px to mm (approx 1px = 0.264583mm)
      const pxToMm = 0.264583;
      // Add margins (10mm each side = 20mm total)
      const pageWidth = (contentWidth * pxToMm) + 20;
      const pageHeight = (contentHeight * pxToMm) + 20;

      const opt = {
        margin: 10,
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: [pageWidth, pageHeight], 
          orientation: 'portrait' 
        }
      };

      await html2pdf().set(opt).from(element).save();

      document.body.removeChild(tempDiv);
      
      setExportStatus("PDF Exported!");
      setTimeout(() => setExportStatus("Export"), 2000);

    } catch (err) {
      console.error("Error generating PDF:", err);
      setExportStatus("Export Failed");
      setTimeout(() => setExportStatus("Export"), 2000);
    } finally {
      setExporting(false);
    }
  };

  const exportToImage = async () => {
    setExporting(true);
    setExportStatus("Generating Image...");
    setShowDropdown(false);

    try {
      const element = prepareExportElement();
      if (!element) return;

      // Temporary container off-screen but visible for canvas
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      // Ensure width fits content for high res
      tempDiv.style.width = '1200px'; 
      tempDiv.appendChild(element);
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(element, {
        scale: 3, // High definition
        useCORS: true,
        logging: false,
        backgroundColor: '#004d2c' // Ensure background color matches
      });

      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      const filename = currentView === 'all' 
        ? 'Package_Comparison.png'
        : `${currentView.charAt(0).toUpperCase() + currentView.slice(1)}_Package.png`;
      
      link.href = image;
      link.download = filename;
      link.click();

      document.body.removeChild(tempDiv);

      setExportStatus("Image Exported!");
      setTimeout(() => setExportStatus("Export"), 2000);

    } catch (err) {
      console.error("Error generating Image:", err);
      setExportStatus("Export Failed");
      setTimeout(() => setExportStatus("Export"), 2000);
    } finally {
      setExporting(false);
    }
  };

  const renderCheckmark = (isIncluded) => (
    <span className={`pcp-checkmark ${isIncluded ? "included" : "excluded"}`}>
      {isIncluded ? "✓" : "✕"}
    </span>
  );

  const renderPin = (title) => {
    const pinnedTitles = ["Immigration", "Immigration Check", "Customs"];
    return pinnedTitles.includes(title) ? <span className="pcp-pin">📌</span> : null;
  };

  const renderRow = (item) => {
    if (currentView === "all") {
      return (
        <div className="pcp-service-row" key={item.name}>
          <div className="pcp-service-name">{item.name}</div>
          <div className="pcp-checkmark-container">
            <div className="pcp-checkmark-wrapper">{renderCheckmark(item.basic)}</div>
            <div className="pcp-checkmark-wrapper">{renderCheckmark(item.medium)}</div>
            <div className="pcp-checkmark-wrapper">{renderCheckmark(item.premium)}</div>
          </div>
        </div>
      );
    } else {
      const isIncluded = item[currentView];
      if (!isIncluded) return null;
      return (
        <div className="pcp-service-row" key={item.name}>
          <div className="pcp-service-name">{item.name}</div>
          <div className="pcp-checkmark-container single">
            <div className="pcp-checkmark-wrapper single">{renderCheckmark(isIncluded)}</div>
          </div>
        </div>
      );
    }
  };

  const renderSection = (sectionTitle, items) => {
    const filteredItems = currentView === "all" 
      ? items 
      : items.filter(item => item[currentView]);

    if (currentView !== "all" && filteredItems.length === 0) return null;

    return (
      <div className="pcp-section" key={sectionTitle}>
        <div className="pcp-section-title-container">
          <div className="pcp-section-title">{sectionTitle}</div>
          {renderPin(sectionTitle)}
        </div>
        <div className="pcp-section-content">
          {filteredItems.map(item => renderRow(item))}
        </div>
      </div>
    );
  };

  const renderColumnHeaders = () => {
    if (currentView === "all") {
      return (
        <div className="pcp-column-headers">
          <div className="pcp-service-header">Services</div>
          <div className="pcp-checkmark-container">
            <div className="pcp-package-header">Basic</div>
            <div className="pcp-package-header">Medium</div>
            <div className="pcp-package-header">Premium</div>
          </div>
        </div>
      );
    }
    return (
      <div className="pcp-column-headers">
        <div className="pcp-service-header">Services</div>
        <div className="pcp-checkmark-container single">
          <div className="pcp-package-header">
            {currentView.charAt(0).toUpperCase() + currentView.slice(1)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pcp-wrapper">
      <div className="pcp-container" ref={containerRef}>
        <div className="pcp-header">
          <h1 className="pcp-header-title">
            {currentView === "all" ? "Package Comparison" : `${currentView.charAt(0).toUpperCase() + currentView.slice(1)} Package Benefits`}
          </h1>
          <div className="pcp-header-controls">
            <div className="pcp-view-toggle">
              {["all", "basic", "medium", "premium"].map((view) => (
                <button
                  key={view}
                  className={`pcp-toggle-btn ${currentView === view ? "active" : ""}`}
                  onClick={() => setCurrentView(view)}
                >
                  {view === "all" ? "All Packages" : view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="pcp-dropdown-container" ref={dropdownRef}>
              <button 
                className="pcp-export-btn" 
                onClick={() => setShowDropdown(!showDropdown)}
                disabled={exporting || loading}
              >
                <span>{exportStatus === "Generating PDF..." || exportStatus === "Generating Image..." ? "⏳" : exportStatus.includes("Exported") ? "✅" : exportStatus.includes("Failed") ? "❌" : "�"}</span>
                <span>{exportStatus}</span>
                <span style={{ fontSize: '10px', marginLeft: '4px' }}>▼</span>
              </button>
              
              {showDropdown && (
                <div className="pcp-dropdown-menu">
                  <button className="pcp-dropdown-item" onClick={exportToPDF} disabled={exporting}>
                    <span>📄</span> Export PDF
                  </button>
                  <button className="pcp-dropdown-item" onClick={exportToImage} disabled={exporting}>
                    <span>🖼️</span> Export Image (HD)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="pcp-content">
            <div className="pcp-loading">
              <div className="pcp-spinner"></div>
              <p style={{ marginTop: "20px" }}>Loading package details...</p>
            </div>
          </div>
        ) : error ? (
          <div className="pcp-content">
            <div className="pcp-empty">{error}</div>
          </div>
        ) : (
          <>
            {renderColumnHeaders()}
            <div className="pcp-content">
              {packageData && Object.entries(packageData).map(([categoryKey, category]) => (
                <div key={categoryKey}>
                  <div className="pcp-category-title">{category.title}</div>
                  {Object.entries(category.sections)
                    .map(([secTitle, items]) => {
                       // Sort items by order first
                       const sortedItems = items.sort((a, b) => (a.order || 0) - (b.order || 0));
                       return [secTitle, sortedItems];
                    })
                    .sort(([, itemsA], [, itemsB]) => {
                       // Sort sections by order of first item
                       const orderA = itemsA[0]?.order || 0;
                       const orderB = itemsB[0]?.order || 0;
                       return orderA - orderB;
                    })
                    .map(([sectionKey, items]) => 
                      renderSection(sectionKey, items)
                    )}
                </div>
              ))}
              {!packageData && <div className="pcp-empty">No services available.</div>}
            </div>
            <div className="pcp-footer">
              <div className="pcp-footer-note">📌 Only Applicable to International Travel</div>
              <div className="pcp-footer-note">* Subject to Airport and Airline approval</div>
              <div className="pcp-footer-note">** Medicine assistance only (must be supplied by the Care Seeker to the Carer)</div>
              <div className="pcp-footer-note">*** Carer will provide Assistance with the Care Seeker's hand luggage</div>
              <div className="pcp-footer-note">**** Carer is not responsible for Care Seeker's luggage contents. Carer only provides Assistance in opening and closing the luggage.</div>
              <div className="pcp-footer-warning">
                Please Note: Keep all your travel documents, valuables and personal belongings with you all the time.
                B4T/Buddy4Travel is not liable for theft or loss of your personal property.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PackageComparisonPreview;
